"use client";
import React, { Dispatch, SetStateAction } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import feedCommentsApi from "@/api/comments/feed/client";
import { CommentInput } from "../../Input";
import { FeedPostDto } from "@/api/posts/types";
import useInfinitePostComments from "../hooks/useInfinitePostComments";
import { Button } from "@/app/(components)/Basic/Button";
import { FeedPostCommentsTree } from "../../CommentsTree/FeedPostCommentsTree";

export interface Props {
  feedPost: FeedPostDto;
  showReply: boolean;
  setShowReply: Dispatch<SetStateAction<boolean>>;
}

export default function FeedPostCommentsList({
  feedPost,
  showReply,
  setShowReply,
}: Props) {
  const queryClient = useQueryClient();

  const { comments, hasNextPage, fetchNextPage } = useInfinitePostComments(
    feedPost.id,
  );

  const { mutateAsync: postFeedComment } = useMutation({
    mutationFn: (text: string) =>
      feedCommentsApi.createPostComment({
        repliedToPostId: feedPost.id!,
        text,
      }),
    onSuccess: () => {
      setShowReply(false);

      queryClient.invalidateQueries(["feed-comments", feedPost.id]);
      queryClient.invalidateQueries(["feed-comment-count", feedPost.id]);
    },
  });

  return (
    <>
      {showReply ? (
        <CommentInput
          iconsSize="big"
          replyText="Reply"
          onCommentPost={async (comment: string) =>
            await postFeedComment(comment)
          }
        />
      ) : null}
      {comments?.length ? (
        <ul className="mt-4 gap-4 md:mt-6 flex flex-col md:gap-6">
          {comments.map((comment) => (
            <FeedPostCommentsTree
              key={comment.id}
              feedPostId={feedPost.id!}
              comment={comment}
              depth={1}
            />
          ))}
        </ul>
      ) : null}
      {hasNextPage ? (
        <div className="flex justify-center">
          <Button native className="text-red " onClick={() => fetchNextPage()}>
            View more
          </Button>
        </div>
      ) : null}
    </>
  );
}
